import { queryOptions } from '@tanstack/react-query';
import { api, apiList } from '@/lib/api-client';
import type {
  EntityGroup,
  EntityValue,
  Equipment,
  EquipmentDetail,
  EquipmentGroup,
  EquipmentType,
} from '@erp/shared';

export const RESOURCES_PAGE_SIZE = 50;

// ── Tipos de recurso ────────────────────────────────────────────────────────
export function equipmentTypesOptions() {
  return queryOptions({
    queryKey: ['erp', 'resources', 'types'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<EquipmentType[]>('/resources/types', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

// ── Grupos de recursos (por cliente) ────────────────────────────────────────
export function equipmentGroupsOptions(clientId?: number) {
  return queryOptions({
    queryKey: ['erp', 'resources', 'groups', { clientId: clientId ?? null }] as const,
    queryFn: async ({ signal }) => {
      const res = await api<EquipmentGroup[]>('/resources/groups', {
        query: { client_id: clientId },
        signal,
      });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

// ── Recursos ────────────────────────────────────────────────────────────────
export interface EquipmentListParams {
  search?: string;
  client_ids?: number[];
  type_ids?: number[];
  group_id?: number;
  active?: boolean;
  limit?: number;
  offset?: number;
}

export function equipmentListOptions(params: EquipmentListParams) {
  return queryOptions({
    queryKey: ['erp', 'resources', 'list', params] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<Equipment>('/resources', { query: { ...params }, signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function equipmentDetailOptions(equipmentId: number) {
  return queryOptions({
    queryKey: ['erp', 'resources', 'detail', equipmentId] as const,
    queryFn: async ({ signal }) => {
      const res = await api<EquipmentDetail>(`/resources/${equipmentId}`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

// ── Campos personalizados do recurso ────────────────────────────────────────
export function equipmentEntitiesOptions(equipmentId: number) {
  return queryOptions({
    queryKey: ['erp', 'resources', 'entities', equipmentId] as const,
    queryFn: async ({ signal }) => {
      const res = await api<EntityValue[]>(`/resources/${equipmentId}/entities`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/**
 * Definições de todos os grupos/campos (para opções de single_select) — sem
 * filtro de applied_in: os valores retornados pelo recurso determinam o que
 * é renderizado; o mapa fieldId→options só precisa cobrir os campos.
 */
export function entityGroupsAllOptions() {
  return queryOptions({
    queryKey: ['erp', 'entities', 'groups', 'all'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<EntityGroup[]>('/entities', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
    staleTime: 60_000,
  });
}

export function errorMessage(err: unknown): string {
  if (
    err !== null &&
    typeof err === 'object' &&
    'error' in err &&
    typeof (err as { error: unknown }).error === 'string'
  ) {
    return (err as { error: string }).error;
  }
  return err instanceof Error ? err.message : 'Erro inesperado';
}
